import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';

import { BRAND_CONFIG } from '@shared/services';

@Component({
  selector: 'app-linx-header',
  templateUrl: './linx-header.component.html',
  styleUrls: ['./linx-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class LinxHeaderComponent {
  /** Per-BA logo URL (KOR-14793); falls back to the default Cateno logo when absent or broken. */
  @Input() set logoUrl(value: string | undefined) {
    this._logoUrl = value;
    this.logoFailed = false; // new BA logo: give it another chance to load
  }
  get logoUrl(): string | undefined {
    return this._logoUrl;
  }
  private _logoUrl?: string;

  @Input() businessName?: string;

  /** Set when the custom logo URL fails to load (404/invalid), so we fall back to the brand default logo. */
  private logoFailed = false;

  /** Host-based default brand (SALT on a salt host, Cateno otherwise). */
  private readonly brand = inject(BRAND_CONFIG);

  get hasCustomLogo(): boolean {
    return !!this._logoUrl && !this.logoFailed;
  }

  get resolvedLogo(): string {
    return this.hasCustomLogo ? this._logoUrl! : this.brand.logo;
  }

  get logoAlt(): string {
    return this.hasCustomLogo ? this.businessName || 'logo' : this.brand.logoAlt;
  }

  /** Text wordmark shown next to the default logo (empty for brands whose logo is itself a wordmark). */
  get defaultWordmark(): string {
    return this.brand.wordmark;
  }

  /**
   * True only for a brand default logo that ships as a light asset (SALT's white
   * wordmark) shown on the light landing card — never for custom workflow logos,
   * which are real images and must render as-is.
   */
  get invertDefaultLogo(): boolean {
    return !this.hasCustomLogo && this.brand.defaultLogoInvert;
  }

  onLogoError(): void {
    this.logoFailed = true;
  }
}
